import React, { useMemo, useContext } from "react";
import { useNavigate } from "react-router-dom";
import * as Yup from "yup";
import { Field, Formik } from "formik";
import { Alert, Button, Form, Row, Col } from "react-bootstrap";
import countryList from "react-select-country-list";
import CountrySelectContext from "../../utils/CountrySelectContext";
import { useDispatch, useSelector } from "react-redux";
import { useWhitelabel } from "../../contexts/WhitelabelContext";
import { createLead } from "../../redux/slices/leads";
import NotyfContext from "../../contexts/NotyfContext";

function SignUp() {
  const { backgroundColor, fontColor } = useWhitelabel();
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const countryOptions = useMemo(() => {
    const options = countryList().getData();
    return [{ label: "Select a country", value: "" }, ...options];
  }, []);
  const notyf = useContext(NotyfContext);
  const demoContact = useSelector((state) => state.demoContacts.demoContact);

  return (
    <Formik
      initialValues={{
        fullName: "",
        country: "",
        email: demoContact.email,
        telephoneNumber: "",
        username: "",
        consentMarketing: demoContact.consentMarketing,
      }}
      validationSchema={Yup.object().shape({
        fullName: Yup.string()
          .max(255, "Full Name must be at most 255 characters")
          .required("Full Name is required"),

        country: Yup.string().max(
          100,
          "Country must be at most 100 characters"
        ),
        email: Yup.string()
          .email("Must be a valid email")
          .max(255, "Email must be at most 255 characters")
          .required("Email is required"),

        telephoneNumber: Yup.string()
          .max(20, "Telephone number must be at most 20 characters")
          .nullable(),

        username: Yup.string()
          .max(255, "Username must be at most 255 characters")
          .required("Username is required"),

        consentMarketing: Yup.boolean(),

        submit: Yup.boolean(),
      })}
      onSubmit={async (values, { setErrors, setStatus, setSubmitting }) => {
        try {
          await createLead({ data: values })
            .then(() => {
              notyf.open({
                type: "success",
                message:
                  "Your request has been received. Please check your email to complete your registration.",
                duration: 5000,
                ripple: true,
                dismissible: false,
                position: {
                  x: "center",
                  y: "top",
                },
              });
            })
            .catch((error) => {
              notyf.open({
                type: "danger",
                message:
                  "Something went wrong. Please try again or contact our support team.",
                duration: 5000,
                ripple: true,
                dismissible: false,
                position: {
                  x: "center",
                  y: "top",
                },
              });
            });
        } catch (error) {
          const message = error.message || "Something went wrong";

          setStatus({ success: false });
          setErrors({ submit: message });
          setSubmitting(false);
        }
      }}
    >
      {({
        errors,
        handleBlur,
        handleChange,
        handleSubmit,
        isSubmitting,
        touched,
        values,
        setValues,
        setFieldValue,
      }) => {
        return (
          <Form onSubmit={handleSubmit}>
            {errors.submit && (
              <Alert className="my-3" variant="danger">
                {errors.submit}
              </Alert>
            )}
            <Form.Group className="mb-3">
              <Form.Label>Full name</Form.Label>
              <Form.Control
                type="text"
                name="fullName"
                placeholder="Full name"
                value={values.fullName}
                isInvalid={Boolean(touched.fullName && errors.fullName)}
                onBlur={handleBlur}
                onChange={handleChange}
              />
              {!!touched.fullName && (
                <Form.Control.Feedback type="invalid">
                  {errors.fullName}
                </Form.Control.Feedback>
              )}
            </Form.Group>
            <Form.Group className="mb-3">
              <Form.Label>Country</Form.Label>
              <CountrySelectContext.Provider value={countryOptions}>
                <CountrySelectContext.Consumer>
                  {(countryOptions) => (
                    <Form.Select
                      value={values.country}
                      onChange={handleChange}
                      name="country"
                      isInvalid={Boolean(touched.country && errors.country)}
                    >
                      {countryOptions.map((option) => (
                        <option key={option.label} value={option.label}>
                          {option.label}
                        </option>
                      ))}
                    </Form.Select>
                  )}
                </CountrySelectContext.Consumer>
              </CountrySelectContext.Provider>
              {!!touched.country && (
                <Form.Control.Feedback type="invalid">
                  {errors.country}
                </Form.Control.Feedback>
              )}
            </Form.Group>
            <Form.Group className="mb-3">
              <Form.Label>Telephone Number</Form.Label>
              <Form.Control
                type="text"
                value={values.telephoneNumber}
                name="telephoneNumber"
                placeholder="Telephone Number"
                isInvalid={Boolean(
                  touched.telephoneNumber && errors.telephoneNumber
                )}
                onBlur={handleBlur}
                onChange={handleChange}
              />
              {!!touched.telephoneNumber && (
                <Form.Control.Feedback type="invalid">
                  {errors.telephoneNumber}
                </Form.Control.Feedback>
              )}
            </Form.Group>
            <Form.Group className="mb-3">
              <Form.Label>Email address</Form.Label>
              <Form.Control
                type="email"
                name="email"
                placeholder="Email address"
                value={values.email}
                isInvalid={Boolean(touched.email && errors.email)}
                onBlur={handleBlur}
                onChange={handleChange}
              />
              {!!touched.email && (
                <Form.Control.Feedback type="invalid">
                  {errors.email}
                </Form.Control.Feedback>
              )}
            </Form.Group>
            <Form.Group className="mb-3">
              <Form.Label>Username</Form.Label>
              <Form.Control
                type="text"
                name="username"
                placeholder="Username"
                value={values.username}
                isInvalid={Boolean(touched.username && errors.username)}
                onBlur={handleBlur}
                onChange={handleChange}
              />
              {!!touched.username && (
                <Form.Control.Feedback type="invalid">
                  {errors.username}
                </Form.Control.Feedback>
              )}
            </Form.Group>
            <Form.Group className="mb-3" controlId="consentMarketing">
              <Field
                name="consentMarketing"
                type="checkbox"
                className="me-2"
                as={Form.Check.Input}
                checked={values.consentMarketing}
                onChange={(e) =>
                  setFieldValue("consentMarketing", e.target.checked)
                }
                style={{
                  "--dynamic-bg-color": "#0E1A2B",
                }}
              />
              <Form.Label>
                I agree to receive marketing communications and updates.
              </Form.Label>
            </Form.Group>
            <div className="text-center mt-3">
              <Button
                type="submit"
                variant="primary"
                size="lg"
                disabled={isSubmitting}
                style={{
                  "--dynamic-bg-color": backgroundColor,
                  border: backgroundColor,
                  "--dynamic-font-color": fontColor,
                  padding: "10px 20px",
                }}
              >
                Sign up
              </Button>
            </div>
          </Form>
        );
      }}
    </Formik>
  );
}

export default SignUp;
